// src/pages/usa/colorado/Morrison.js
import "../../../App.css";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import BackOnly from "../../../components/BackOnly";
import ContactPopup from "../../../components/ContactPopup";
import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";   // ⭐ REQUIRED

import AffiliateBooking from "../../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../../components/affiliate/AffiliateTrip";

// ⭐ STRICT COMMAND: Morrison ALWAYS goes back to Colorado map
export const backRoute = "/map/colorado";

export default function Morrison() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly (NO LOOPS)
  useEffect(() => {
    if (!window.__morrisonBackRouteSet) {
      window.__morrisonBackRouteSet = true;
      navigate(".", { replace: true, state: { backRoute } });
    }
  }, [navigate]);

  return (

    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>


      <div className="page-container" style={{ padding: "12px" }}>
        <h1>Morrison, Colorado</h1>

        <section className="travel-review">
          {/* LEFT COLUMN — TEXT */}
          <div className="travel-text">
            <h2>Discover Morrison, Colorado</h2>

            <p>
              Morrison, Colorado, is a charming small town just a short drive from Denver. Known for its rich history,
              stunning natural beauty, and vibrant arts scene, Morrison offers a unique blend of small-town warmth and
              outdoor adventure. Home to the iconic <strong>Red Rocks Amphitheatre</strong>, it draws music lovers and
              nature enthusiasts alike.
            </p>

            <p>
              The area features a variety of hiking trails, including the popular <strong>Red Rocks Trail</strong>,
              which offers sweeping views of the surrounding landscape. Visitors can also explore nearby{" "}
              <strong>Dinosaur Ridge</strong>, where real dinosaur footprints and fossil beds reveal the area's
              prehistoric past.
            </p>

            <p>
              Morrison is also part of the scenic <strong>Lariat Loop Scenic & Historic Byway</strong> — a 40-mile route
              connecting <strong>Golden, Morrison, and Evergreen</strong>. The loop offers breathtaking views of the
              Rocky Mountains and access to charming mountain towns, historic sites, and natural landmarks.
            </p>

            {/* 🚗 GETTING THERE */}
            <h3>🚗 Getting There</h3>

            <p>
              <strong>Denver → Morrison:</strong> Take C‑470 West to Exit 259, then follow Highway 74 into town.
            </p>
            <p>
              <strong>Golden → Morrison:</strong> Take Hwy 93 → Hwy 6 → Hwy 74.
            </p>
            <p>
              <strong>Evergreen → Morrison:</strong> Take Hwy 74 East — a scenic, mountain‑view drive.
            </p>

            {/* 🌟 MY PERSONAL OBSERVATION */}
            <h3>🌟 My Personal Observation</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Red Rocks Amphitheatre:</strong> A world‑famous outdoor venue with unmatched acoustics and
                  breathtaking scenery. The surrounding trails offer incredible views and range from easy walks to
                  challenging climbs.<br />
                  <a
                    href="https://www.google.com/maps?q=39.665545658781234,-105.20529830486839"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Red Rocks Amphitheatre
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Morrison Town:</strong> A small but vibrant community with restaurants, shops, galleries, and
                  friendly locals. Popular with hikers, bikers, and motorcycle riders exploring the foothills.<br />
                  <a
                    href="https://www.google.com/maps?q=39.65373524614154,-105.190874282598"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Morrison Town
                  </a>
                  <br />
                  <strong>Ride the Lariat Loop:</strong>{" "}
                  <a
                    href="https://www.google.com/maps/dir/Golden,+CO/Lookout+Mountain,+Golden,+CO/Genesee+Park,+Golden,+CO/Evergreen,+CO/Morrison,+CO/Red+Rocks+Park+and+Amphitheatre,+Morrison,+CO/Dinosaur+Ridge,+Morrison,+CO/Golden,+CO"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Tap here for mobile‑friendly route
                  </a>
                </div>
              </li>
            </ul>

            {/* 🏔️ HIDDEN GEMS */}
            <h3>🏔️ Hidden Gems in Morrison</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Colorow’s Cave:</strong> A historic cave tied to Chief Colorow of the Ute tribe.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Morrison Natural History Museum:</strong> Dinosaur tracks, fossils, and hands‑on exhibits.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Tiny Town & Railroad:</strong> A miniature village with a working train.<br />
                  <a
                    href="https://www.google.com/maps?q=39.603223171707974, -105.22343454524422"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Tiny Town & Railroad
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Walker Mansion Ruins:</strong> Scenic stone ruins above Morrison.<br />
                  <a
                    href="https://www.google.com/maps?q=39.635271246644315, -105.22700038649106"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 Walker Mansion Ruins
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Compassionate Dharma Cloud Monastery:</strong> A quiet Buddhist retreat in the foothills.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Bear Creek Trail:</strong> A peaceful creekside walk through Morrison.
                </div>
              </li>
            </ul>

            {/* 🛏️ PLACES TO STAY */}
            <h3>🛏️ Places to Stay Near Morrison</h3>

            <p>
              Morrison is surrounded by excellent lodging options in Golden, Lakewood, and Littleton. These top‑ranked
              stays consistently earn high marks:
            </p>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Cliff House Lodge B&amp;B (Morrison):</strong> Historic, cozy, themed cottages with hot tubs.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Origin Hotel Red Rocks (Golden):</strong> Official Red Rocks partner hotel with shuttle
                  service.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>La Quinta Inn &amp; Suites (Littleton):</strong> Clean, affordable, and close to Morrison.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Hampton Inn Denver–West/Golden:</strong> Reliable and close to the Lariat Loop.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>SpringHill Suites (Lakewood):</strong> Spacious suites near trails and restaurants.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Private Basement Stay (Morrison):</strong> A cozy, well‑reviewed local stay.
                </div>
              </li>
            </ul>

            {/* AFFILIATE BLOCK */}
            <h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
                        <div className="affiliate-block">
                          <AffiliateExpedia />
                          <AffiliateBooking />
                          <AffiliateTrip />
                        </div>

            {/* 🎥 VIDEOS */}
            <h3>🎥 My Travel Videos</h3>

            <p>Here are two videos I recorded in Morrison and Red Rocks:</p>

            <p>
              <a
                href="https://youtu.be/wduP0CUEgi0?si=28FVIPmCvUx5JRjL"
                target="_blank"
                rel="noopener noreferrer"
              >
                Red Rocks Amphitheatre
              </a>
              <br />
              <a
                href="https://youtu.be/txVRehsdu88?si=EC2jwZ-hkn7q5Seq"
                target="_blank"
                rel="noopener noreferrer"
              >
                Morrison Town
              </a>

               <br />
              <a
                href="https://youtu.be/P5M4bBeecWc?si=5TIWuMNEqtKY3TJy"
                target="_blank"
                rel="noopener noreferrer"
              >
                Walker Mansion Ruins
              </a>



            </p>
          </div>

          {/* RIGHT COLUMN — IMAGES */}
          <div className="travel-image-column">

             <a
              href="https://youtu.be/P5M4bBeecWc?si=5TIWuMNEqtKY3TJy"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/colorado/morrison/walkermansionruin.jpg"
                alt="Walker Mansion Ruins Video"
              />
            </a>



            <a
              href="https://youtu.be/wduP0CUEgi0?si=28FVIPmCvUx5JRjL"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/colorado/morrison/theamphitheatre.jpg"
                alt="Red Rocks Amphitheatre Video"
              />
            </a>

            <a
              href="https://youtu.be/txVRehsdu88?si=EC2jwZ-hkn7q5Seq"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img
                src="/images/usa/colorado/morrison/thetown.jpg"
                alt="Morrison Town Video"
              />
            </a>

            <img src="/images/usa/colorado/morrison/redrocktraidingpost.jpg" alt="Red Rocks Trading Post" />
            <img src="/images/usa/colorado/morrison/houseatredrocks.jpg" alt="House at Red Rocks" />
            <img src="/images/usa/colorado/morrison/housefromdistance.jpg" alt="House from distance" />
            <img src="/images/usa/colorado/morrison/redrocksstairs.jpg" alt="The Stairs at Red Rocks" />
            <img src="/images/usa/colorado/morrison/amazingviewofredrock.jpg" alt="Amazing view of Red Rock" />
            <img src="/images/usa/colorado/morrison/rocksfromtrail.jpg" alt="Rocks from trail" />
            <img src="/images/usa/colorado/morrison/therock.jpg" alt="The Rock from Balcony" />
            <img src="/images/usa/colorado/morrison/redrocksduringnight.jpg" alt="Red Rocks during night" />
            <img src="/images/usa/colorado/morrison/viewofredrockfromdistance.jpg" alt="View of Red Rock from distance" />
            <img src="/images/usa/colorado/morrison/abc.jpg" alt="Thunderhead Balcony" />
            <img src="/images/usa/colorado/morrison/paintonthewall.jpg" alt="Paint on the wall" />
            <img src="/images/usa/colorado/morrison/theroad.jpg" alt="The Road to Morrison" />
          </div>
        </section>
      </div>

      {/* Centered email button */}
      <div className="email-wrapper">
        <button className="email-link" onClick={() => setOpen(true)}>
          contact@thefindingspot.com
        </button>
      </div>

      {/* Popup */}
      <ContactPopup isOpen={open} onClose={() => setOpen(false)} />

      <Footer />
    </>
  );
}
